import React, { useContext, useState } from "react";
import { AppContext } from "../../../Util/contexts/AppContext";
import { ExamCreatorContext } from "../../../Util/contexts/ExamCreatorContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClipboardList } from "@fortawesome/free-solid-svg-icons";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { createPortal } from "react-dom";
import ButtonListPopup from "../../../Components/ButtonListPopup/ButtonListPopup";

const IncompleteExamListCard = ({
  examData,
  selectedExamId,
  setSelectedExamId,
  forceEllipses,
}) => {
  const { userData, selectedTheme, reviewLaterExams, mousePosition } =
    useContext(AppContext);
  const { setIsCaseDetailsPopupDisplayed, setCaseDetails } =
    useContext(ExamCreatorContext);

  const [showMoreSectionOptions, setShowMoreSectionOptions] = useState(false);

  const examId = examData.ExamId || examData.examId;
  const examTitle = examData.ExamTitle || examData.ExamName;
  const examDescription =
    examData.ExamDescription || `Custom Exam - ${examData.ExamName}`;
  const incompleteAttempts = examData.IncompleteAttempts || [];
  const isSelected = selectedExamId === examId;
  const isReviewLater = reviewLaterExams[examId];

  // Get the most recent incomplete attempt
  const mostRecentAttempt = incompleteAttempts.reduce((latest, current) => {
    if (!latest) return current;
    return new Date(current.TimeAttempted) > new Date(latest.TimeAttempted)
      ? current
      : latest;
  }, null);

  // Format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSectionClick = () => {
    if (isSelected) {
      setSelectedExamId(null);
    } else {
      setSelectedExamId(examId);
      // Scroll to the selected card
      setTimeout(() => {
        document.getElementById(`section-${examId}`)?.scrollIntoView({
          behavior: userData.AnimationsDisabled ? "auto" : "smooth",
          block: "center",
        });
      }, 50);
    }
  };

  const handleViewGrades = (e) => {
    e.stopPropagation();
    setShowMoreSectionOptions(false);
    setCaseDetails({
      ...examData,
      examId: examId,
      examTitle: examTitle,
      examDescription: examDescription,
      isIncomplete: true,
    });
    setIsCaseDetailsPopupDisplayed(true);
  };

  const getSectionsCount = () => {
    if (mostRecentAttempt && mostRecentAttempt.IncludedSectionIds) {
      return Array.isArray(mostRecentAttempt.IncludedSectionIds)
        ? mostRecentAttempt.IncludedSectionIds.length
        : mostRecentAttempt.IncludedSectionIds.split(",").length;
    }
    return 0;
  };

  const hasAttempts = incompleteAttempts.length > 0;

  return (
    <div
      id={`section-${examId}`}
      className={`caseListSectionContainer${
        isSelected ? " caseListCaseSelected" : ""
      }`}
      key={"section - " + examTitle + " - " + examId}
    >
      <div
        className={`caseListSectionCheckboxWrapper ${
          isSelected ? "caseListSectionCheckboxSelected" : ""
        }`}
        style={{
          borderLeft: isReviewLater
            ? `4px solid ${selectedTheme.warningColor}`
            : "4px solid transparent",
        }}
        onClick={() => handleSectionClick()}
      >
        <button className="caseListSectionCheckbox">
          {isSelected && (
            <FontAwesomeIcon
              icon={faCheck}
              className="caseListSectionCheckboxIcon"
            />
          )}
        </button>
      </div>
      <div
        className={`caseListSectionContentWrapper ${
          forceEllipses ? "caseListSectionContentWrapper--ellipses" : ""
        }`}
        onClick={() => handleSectionClick()}
      >
        <div
          className={`caseListSectionDetailsWrapper ${
            hasAttempts
              ? "caseListSectionDetailsWrapper--with-attempts"
              : "caseListSectionDetailsWrapper--no-attempts"
          }`}
        >
          <FontAwesomeIcon
            icon={faClipboardList}
            className="caseListSectionDetailsIcon"
          />
          <h3 className="caseListSectionDetailsTitle">
            {examTitle}
            {isReviewLater && (
              <span className="incompleteExamReviewLaterBadge">
                Review Later
              </span>
            )}
          </h3>
          <p className="caseListSectionDetailsSubtitle">{examDescription}</p>
        </div>
        {hasAttempts && (
          <p className="caseListSectionCompletionText">
            Last Attempt: {formatDate(mostRecentAttempt.TimeAttempted)} (
            {incompleteAttempts.length} attempt
            {incompleteAttempts.length !== 1 ? "s" : ""})
            {getSectionsCount() > 0 && ` | ${getSectionsCount()} sections`}
          </p>
        )}
      </div>
      <button
        className={`caseListSectionMobileMoreOptionsButton ${
          forceEllipses ? "caseListSectionMobileMoreOptionsButton--visible" : ""
        }`}
        onClick={(e) => {
          e.stopPropagation();
          setShowMoreSectionOptions(!showMoreSectionOptions);
        }}
      >
        <FontAwesomeIcon
          icon={faEllipsisVertical}
          className="caseListSectionMobileMoreOptionsIcon"
        />
      </button>
      {showMoreSectionOptions &&
        createPortal(
          <ButtonListPopup
            showButtonListPopup={showMoreSectionOptions}
            setShowButtonListPopup={setShowMoreSectionOptions}
            mousePos={mousePosition}
            key={`sectionMoreOptionsPopup - ${examId}`}
            childKey={`sectionMoreOptionsPopupChild - ${examId}`}
          >
            {hasAttempts && (
              <button
                className="moreOptionsPopupButton"
                onClick={handleViewGrades}
              >
                View Details
              </button>
            )}
          </ButtonListPopup>,
          document.body
        )}
    </div>
  );
};

export default IncompleteExamListCard;
